import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form, Input, Modal } from 'antd'
import { ModalProps } from 'antd/lib'
import { useEffect } from 'react'

import {
  useInstitutionServiceCreateInstitutionDepartmentEndpoint,
  useInstitutionServiceGetInstitutionDepartmentEndpoint,
  useInstitutionServiceGetInstitutionDepartmentListEndpointKey,
  useInstitutionServiceGetInstitutionDepartmentUserListEndpointKey,
  useInstitutionServiceGetInstitutionEndpoint,
  useInstitutionServiceUpdateInstitutionDepartmentEndpoint
} from 'src/api/queries'
import { ApiError, ClassifierCategory, ClassifierType } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  ModalFooterProps,
  ClassifierInfiniteSelect,
  SaveConfirm
} from 'src/components'
import { useClassifierId } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { UsersSearch } from './UsersSearch'

type Props = {
  id?: string
  institutionId?: string
  onCancel: () => void
} & Omit<ModalProps, 'onCancel'>

const Header = ({ onCancel, onSubmit, id }: ModalFooterProps) => {
  const { t } = useLocales()

  return (
    <Flex align="center" justify="center" gap={8}>
      <Button onClick={onCancel}>{t('common.cancel')}</Button>

      <SaveConfirm
        title={id ? `${t('common.save')}?` : `${t('common.add')}?`}
        onConfirm={onSubmit}
      >
        <Button type="primary">
          {id ? t('common.save') : t('common.add')}
        </Button>
      </SaveConfirm>
    </Flex>
  )
}

export const AddDepartmentModal = ({ id, onCancel, institutionId }: Props) => {
  const queryClient = useQueryClient()

  const { t } = useLocales()

  const [form] = Form.useForm()

  const { data: institutionData } = useInstitutionServiceGetInstitutionEndpoint(
    {
      id: String(institutionId)
    },
    [String(institutionId)],
    {
      enabled: !!institutionId
    }
  )

  const { data: departmentData, isLoading } =
    useInstitutionServiceGetInstitutionDepartmentEndpoint(
      {
        id: String(institutionId),
        departmentId: String(id)
      },
      [String(id), String(institutionId)],
      {
        enabled: !!id && !!institutionId
      }
    )

  const departmentsGroupId = useClassifierId(
    ClassifierType.DEPARTMENT_GROUP,
    ClassifierCategory.INSTITUTION
  )

  const { mutate: editDepartment } =
    useInstitutionServiceUpdateInstitutionDepartmentEndpoint({
      onError: (errors: ApiError) => {
        formErrorHandler(errors.body.errors)
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            useInstitutionServiceGetInstitutionDepartmentListEndpointKey
          ]
        })
        queryClient.invalidateQueries({
          queryKey: [
            useInstitutionServiceGetInstitutionDepartmentUserListEndpointKey
          ]
        })
        onCancel()
      }
    })

  const { mutate: addDepartment } =
    useInstitutionServiceCreateInstitutionDepartmentEndpoint({
      onError: (errors: ApiError) => {
        formErrorHandler(errors.body.errors)
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            useInstitutionServiceGetInstitutionDepartmentListEndpointKey
          ]
        })
        onCancel()
      }
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const payload = {
      id: String(institutionData?.id),
      departmentId: String(id),
      requestBody: {
        name: values.name,
        departmentGroupId: values.departmentGroupId,
        userProfileIds: values.userProfileIds
      }
    }

    id ? editDepartment(payload) : addDepartment(payload)
  }

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  useEffect(() => {
    if (id && departmentData) {
      form.setFieldsValue({
        name: departmentData?.name,
        departmentGroupId: departmentData?.departmentGroupId
      })
    }
  }, [id, departmentData])

  return (
    <Modal
      maskClosable={false}
      open
      title={
        <Flex gap={8} justify="space-between" align="center">
          {t(
            `organizational_structure.department_tab.${id ? 'modal_title_edit' : 'modal_title'}`
          )}
          {Header({ onCancel: handleCancel, onSubmit, id })}
        </Flex>
      }
      loading={isLoading}
      onCancel={handleCancel}
      closable={false}
      footer={null}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Flex vertical gap={16}>
          <Form.Item
            label={t('form.medical_institution')}
            name="institutionName"
            initialValue={institutionData?.name}
          >
            <Input placeholder={t('form.medical_institution')} disabled />
          </Form.Item>
          <Form.Item
            label={t('organizational_structure.department_tab.title')}
            name="name"
            rules={requiredRules}
          >
            <Input
              placeholder={t(
                'organizational_structure.department_tab.title_placeholder'
              )}
            />
          </Form.Item>
          <Form.Item
            label={t(
              'organizational_structure.department_tab.department_group'
            )}
            name="departmentGroupId"
            rules={requiredRules}
          >
            <ClassifierInfiniteSelect
              id={departmentsGroupId}
              placeholder={t(
                'organizational_structure.department_tab.select_from_list'
              )}
            />
          </Form.Item>

          <Form.Item
            label={t('organizational_structure.department_tab.add_users')}
            name="userProfileIds"
            rules={requiredRules}
          >
            <UsersSearch
              placeholder={t(
                'organizational_structure.department_tab.search_placeholder'
              )}
              form={form}
              departmentId={departmentData?.id}
              institutionId={institutionData?.id}
            />
          </Form.Item>
        </Flex>
      </Form>
    </Modal>
  )
}
